import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade, Parallax } from 'swiper/modules';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight, Loader, Sparkles, ChevronDown, Play } from 'lucide-react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import { getPortfolio } from '../services/contentService';

// Floating Particles Component
const FloatingParticles = () => {
  const particles = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    size: Math.random() * 4 + 1,
    duration: Math.random() * 20 + 15,
    delay: Math.random() * 5,
    x: Math.random() * 100,
    y: Math.random() * 100,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full bg-gold/30"
          style={{
            width: particle.size,
            height: particle.size,
            left: `${particle.x}%`,
            top: `${particle.y}%`,
          }}
          animate={{
            y: [0, -100, 0],
            x: [0, Math.random() * 50 - 25, 0],
            opacity: [0, 0.8, 0],
            scale: [0, 1, 0],
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            delay: particle.delay,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
};

// Mouse Spotlight Effect
const MouseSpotlight = () => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  const springX = useSpring(mouseX, { stiffness: 50, damping: 20 });
  const springY = useSpring(mouseY, { stiffness: 50, damping: 20 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      mouseX.set(e.clientX);
      mouseY.set(e.clientY);
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [mouseX, mouseY]);

  return (
    <motion.div
      className="fixed inset-0 pointer-events-none z-10"
      style={{
        background: useTransform(
          [springX, springY],
          ([x, y]) => `radial-gradient(600px circle at ${x}px ${y}px, rgba(212, 175, 55, 0.06), transparent 40%)`
        ),
      }}
    />
  );
};

// Animated Text with Glitch Effect
const GlitchText = ({ children, className }) => {
  return (
    <span className={`relative inline-block ${className}`}>
      <motion.span
        className="absolute inset-0 text-gold/50"
        animate={{
          x: [0, -2, 2, 0],
          opacity: [0, 1, 0],
        }}
        transition={{
          duration: 0.2,
          repeat: Infinity,
          repeatDelay: 3,
        }}
        aria-hidden="true"
      >
        {children}
      </motion.span>
      <motion.span
        className="absolute inset-0 text-white/50"
        animate={{
          x: [0, 2, -2, 0],
          opacity: [0, 1, 0],
        }}
        transition={{
          duration: 0.2,
          repeat: Infinity,
          repeatDelay: 3,
          delay: 0.1,
        }}
        aria-hidden="true"
      >
        {children}
      </motion.span>
      <span className="relative">{children}</span>
    </span>
  );
};

// Split Text Animation
const SplitText = ({ text, className, delay = 0 }) => {
  const letters = text.split('');
  
  return (
    <span className={className}>
      {letters.map((letter, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 50, rotateX: -90 }}
          animate={{ opacity: 1, y: 0, rotateX: 0 }}
          transition={{
            duration: 0.5,
            delay: delay + i * 0.03,
            ease: "easeOut",
          }}
          className="inline-block"
          style={{ transformOrigin: 'bottom' }}
        >
          {letter === ' ' ? '\u00A0' : letter}
        </motion.span>
      ))}
    </span>
  );
};

// Animated Counter
const AnimatedCounter = ({ value, suffix = '', duration = 2 }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    const num = parseInt(value);
    const step = num / (duration * 60);
    let current = 0;
    
    const timer = setInterval(() => {
      current += step;
      if (current >= num) {
        setCount(num);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, 1000 / 60);
    
    return () => clearInterval(timer);
  }, [value, duration]);
  
  return <span>{count}{suffix}</span>;
};

// Morphing Background Shapes
const MorphingShapes = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Large Morphing Shape 1 */}
      <motion.div
        className="absolute top-0 left-0 w-[800px] h-[800px]"
        animate={{
          borderRadius: [
            "60% 40% 30% 70% / 60% 30% 70% 40%",
            "30% 60% 70% 40% / 50% 60% 30% 60%",
            "60% 40% 30% 70% / 60% 30% 70% 40%",
          ],
          rotate: [0, 180, 360],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear",
        }}
        style={{
          background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, transparent 50%)',
          transform: 'translate(-50%, -50%)',
        }}
      />
      
      {/* Large Morphing Shape 2 */}
      <motion.div
        className="absolute bottom-0 right-0 w-[600px] h-[600px]"
        animate={{
          borderRadius: [
            "40% 60% 70% 30% / 40% 50% 60% 50%",
            "70% 30% 50% 50% / 30% 30% 70% 70%",
            "40% 60% 70% 30% / 40% 50% 60% 50%",
          ],
          rotate: [360, 180, 0],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: "linear",
        }}
        style={{
          background: 'linear-gradient(225deg, rgba(212, 175, 55, 0.15) 0%, transparent 50%)',
          transform: 'translate(30%, 30%)',
        }}
      />
    </div>
  );
};

// Animated Grid Background
const AnimatedGrid = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <motion.div
        className="absolute inset-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.03 }}
        transition={{ duration: 2 }}
        style={{
          backgroundImage: `
            linear-gradient(rgba(212, 175, 55, 0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(212, 175, 55, 0.3) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />
      {/* Animated scanline */}
      <motion.div
        className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent"
        animate={{
          y: ['-100vh', '100vh'],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "linear",
        }}
      />
    </div>
  );
};

const HeroSlider = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const data = await getPortfolio();
        const featured = data.filter(p => p.featured);
        setProjects(featured.length > 0 ? featured : data.slice(0, 5));
      } catch (error) {
        console.error('Error loading hero projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  if (loading) {
    return (
      <section className="hero-section relative h-screen w-full overflow-hidden bg-black flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <Loader className="w-16 h-16 text-gold" />
        </motion.div>
      </section>
    );
  }

  if (projects.length === 0) {
    return (
      <section className="hero-section relative h-screen w-full overflow-hidden bg-black">
        <MouseSpotlight />
        <MorphingShapes />
        <AnimatedGrid />
        <FloatingParticles />

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          {/* Logo/Brand Mark */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", duration: 1.5, delay: 0.2 }}
            className="mb-8"
          >
            <motion.div
              className="w-24 h-24 border-4 border-gold rounded-2xl flex items-center justify-center rotate-45"
              whileHover={{ scale: 1.1, rotate: 135 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Sparkles className="w-12 h-12 text-gold -rotate-45" />
            </motion.div>
          </motion.div>

          {/* Main Title with Split Animation */}
          <motion.h1 className="text-6xl md:text-8xl lg:text-9xl font-bold mb-6">
            <SplitText text="ADK" className="text-white" delay={0.5} />{" "}
            <SplitText 
              text="Creative" 
              className="text-transparent bg-clip-text bg-gradient-to-r from-gold via-gold-light to-gold" 
              delay={0.7} 
            />
          </motion.h1>

          {/* Subtitle */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mb-8"
          >
            <p className="text-xl md:text-3xl text-gray-400 font-light tracking-wide">
              Grafik Tasarım & Marka Kimliği Tasarımı
            </p>
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1, delay: 1.5 }}
              className="h-px bg-gradient-to-r from-transparent via-gold to-transparent mt-4"
            />
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.8 }}
            className="flex flex-wrap gap-4 justify-center"
          >
            <motion.a
              href="#portfolio"
              className="group px-8 py-4 bg-gold text-black font-semibold rounded-full hover:bg-gold-light transition-all duration-300 shadow-lg hover:shadow-gold/50 flex items-center gap-2"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              Portfolyoyu İncele
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </motion.a>
            
            <motion.a
              href="#paketler"
              className="px-8 py-4 bg-white/5 backdrop-blur-sm border border-white/10 text-white font-semibold rounded-full hover:bg-white/10 hover:border-gold/30 transition-all duration-300"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              Paketleri Gör
            </motion.a>
          </motion.div>

          {/* Animated Stats */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 2.2 }}
            className="absolute bottom-20 left-0 right-0"
          >
            <div className="flex flex-wrap justify-center gap-16 text-center">
              {[
                { value: '50', suffix: '+', label: 'Proje' },
                { value: '30', suffix: '+', label: 'Müşteri' },
                { value: '5', suffix: '+', label: 'Yıl' },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 2.4 + i * 0.2 }}
                >
                  <div className="text-4xl font-bold text-gold mb-1">
                    <AnimatedCounter value={stat.value} suffix={stat.suffix} duration={2} />
                  </div>
                  <div className="text-sm text-gray-500 uppercase tracking-wider">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.a
            href="#about"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 2.5 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="flex flex-col items-center gap-2 text-gray-500 hover:text-gold transition-colors cursor-pointer"
            >
              <span className="text-xs uppercase tracking-wider">Scroll</span>
              <ChevronDown className="w-5 h-5" />
            </motion.div>
          </motion.a>
        </div>
      </section>
    );
  }

  const featuredProjects = projects;

  return (
    <section className="hero-section relative h-screen w-full overflow-hidden bg-black">
      <MouseSpotlight />
      <FloatingParticles />
      
      <Swiper
        ref={swiperRef}
        modules={[Navigation, Pagination, Autoplay, EffectFade, Parallax]}
        slidesPerView={1}
        spaceBetween={0}
        loop={true}
        speed={1500}
        parallax={true}
        autoplay={{
          delay: 6000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        effect="fade"
        fadeEffect={{
          crossFade: true,
        }}
        navigation={{
          prevEl: '.hero-prev',
          nextEl: '.hero-next',
        }}
        pagination={{
          el: '.hero-pagination',
          clickable: true,
          renderBullet: (index, className) => {
            return `<span class="${className}"></span>`;
          },
        }}
        onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
        className="h-full w-full"
      >
        {featuredProjects.map((project, index) => (
          <SwiperSlide key={project.id}>
            {({ isActive }) => (
              <div className="relative h-full w-full" style={{ backgroundColor: project.bgColor }}>
                {/* Background Image with Enhanced Parallax */}
                <div 
                  className="absolute inset-0 z-0"
                  data-swiper-parallax="-30%"
                >
                  {/* Multi-layer Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent z-10" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30 z-10" />
                  
                  {/* Animated Image */}
                  <motion.img
                    src={project.image}
                    alt={project.title}
                    className="h-full w-full object-cover"
                    initial={{ scale: 1.2, opacity: 0.4 }}
                    animate={isActive ? { scale: 1, opacity: 0.7 } : { scale: 1.2, opacity: 0.4 }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                  />
                  
                  {/* Noise Texture Overlay */}
                  <div 
                    className="absolute inset-0 z-20 opacity-[0.02] mix-blend-overlay"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%' height='100%' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                    }}
                  />
                </div>

                {/* Content */}
                <div className="relative z-20 h-full flex items-center">
                  <div className="container mx-auto px-6 md:px-12">
                    <AnimatePresence mode="wait">
                      {isActive && (
                        <motion.div
                          key={project.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.5 }}
                          className="max-w-3xl"
                        >
                          {/* Project Number with Line Animation */}
                          <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="mb-6 flex items-center gap-4"
                          >
                            <motion.div
                              initial={{ scaleX: 0 }}
                              animate={{ scaleX: 1 }}
                              transition={{ duration: 0.8, delay: 0.4 }}
                              className="w-12 h-px bg-gold origin-left"
                            />
                            <span className="text-gold font-mono text-sm tracking-wider">
                              PROJECT {String(index + 1).padStart(2, '0')}
                            </span>
                          </motion.div>

                          {/* Title with Stagger Animation */}
                          <motion.h1
                            data-swiper-parallax="-300"
                            className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-tight"
                          >
                            <GlitchText>
                              <SplitText text={project.title} delay={0.3} />
                            </GlitchText>
                          </motion.h1>

                          {/* Description with Fade */}
                          <motion.p
                            data-swiper-parallax="-200"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.8 }}
                            className="text-lg md:text-xl text-gray-300 mb-8 max-w-2xl leading-relaxed"
                          >
                            {project.description}
                          </motion.p>

                          {/* Tags with Stagger */}
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5, delay: 1 }}
                            className="flex flex-wrap gap-3 mb-8"
                          >
                            {project.tags.map((tag, i) => (
                              <motion.span
                                key={i}
                                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 1 + i * 0.1 }}
                                whileHover={{ scale: 1.05, y: -2 }}
                                className="px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-sm text-white/90 hover:bg-gold/20 hover:border-gold transition-all duration-300 cursor-default"
                              >
                                {tag}
                              </motion.span>
                            ))}
                          </motion.div>

                          {/* CTA Button */}
                          <motion.button
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 1.3 }}
                            whileHover={{ scale: 1.05, y: -3 }}
                            whileTap={{ scale: 0.95 }}
                            className="group inline-flex items-center gap-3 px-8 py-4 bg-gold text-black font-semibold rounded-full hover:bg-gold-light transition-all duration-300 shadow-lg hover:shadow-gold/50"
                          >
                            <span>Projeyi İncele</span>
                            <motion.div
                              animate={{ x: [0, 5, 0] }}
                              transition={{ duration: 1.5, repeat: Infinity }}
                            >
                              <ArrowRight className="w-5 h-5" />
                            </motion.div>
                          </motion.button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {/* Project Info Overlay - Enhanced */}
                <AnimatePresence>
                  {isActive && (
                    <motion.div
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 50 }}
                      transition={{ duration: 0.8, delay: 1.5 }}
                      className="absolute bottom-12 right-12 z-20 hidden lg:block"
                    >
                      <motion.div
                        className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 min-w-[220px]"
                        whileHover={{ scale: 1.02, y: -5 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        <div className="space-y-4">
                          <div>
                            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Client</div>
                            <div className="text-white font-semibold text-lg">{project.client}</div>
                          </div>
                          <div className="w-full h-px bg-white/10" />
                          <div>
                            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Year</div>
                            <div className="text-gold font-bold text-2xl">{project.year}</div>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Enhanced Navigation */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex items-center gap-8">
        <motion.button
          className="hero-prev group p-4 rounded-full bg-white/5 backdrop-blur-md border border-white/10 hover:bg-gold hover:border-gold transition-all duration-300"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <ChevronLeft className="w-6 h-6 text-white group-hover:text-black transition-colors" />
        </motion.button>
        
        {/* Progress Indicator */}
        <div className="flex items-center gap-3">
          {featuredProjects.map((_, i) => (
            <motion.div
              key={i}
              className="relative h-1 rounded-full overflow-hidden cursor-pointer"
              style={{ width: activeIndex === i ? 40 : 12 }}
              animate={{ width: activeIndex === i ? 40 : 12 }}
              transition={{ duration: 0.3 }}
              onClick={() => swiperRef.current?.swiper?.slideToLoop(i)}
            >
              <div className="absolute inset-0 bg-white/20" />
              {activeIndex === i && (
                <motion.div
                  className="absolute inset-0 bg-gold"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 6, ease: "linear" }}
                  style={{ transformOrigin: 'left' }}
                />
              )}
            </motion.div>
          ))}
        </div>
        
        <motion.button
          className="hero-next group p-4 rounded-full bg-white/5 backdrop-blur-md border border-white/10 hover:bg-gold hover:border-gold transition-all duration-300"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <ChevronRight className="w-6 h-6 text-white group-hover:text-black transition-colors" />
        </motion.button>
      </div>

      {/* Slide Counter */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2 }}
        className="absolute top-1/2 right-8 -translate-y-1/2 z-30 hidden xl:flex flex-col items-center gap-4"
      >
        <span className="text-gold font-mono text-2xl font-bold">
          {String(activeIndex + 1).padStart(2, '0')}
        </span>
        <div className="w-px h-20 bg-white/20 relative overflow-hidden">
          <motion.div
            className="absolute top-0 left-0 w-full bg-gold"
            style={{ height: `${((activeIndex + 1) / featuredProjects.length) * 100}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        <span className="text-gray-500 font-mono text-sm">
          {String(featuredProjects.length).padStart(2, '0')}
        </span>
      </motion.div>

      {/* Enhanced Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2 }}
        className="absolute bottom-8 left-8 z-30 hidden md:block"
      >
        <motion.a
          href="#about"
          className="flex flex-col items-center gap-3 text-gray-400 hover:text-gold transition-colors cursor-pointer group"
        >
          <motion.span
            className="text-xs tracking-[0.3em] uppercase"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            Scroll
          </motion.span>
          <motion.div
            className="w-px h-20 bg-gradient-to-b from-gold via-gold/50 to-transparent relative overflow-hidden"
          >
            <motion.div
              className="absolute top-0 left-0 w-full h-8 bg-gradient-to-b from-gold to-transparent"
              animate={{ y: ['-100%', '300%'] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>
        </motion.a>
      </motion.div>
    </section>
  );
};

export default HeroSlider;
